import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaUsers, 
  FaSearch, 
  FaArrowLeft,
  FaUserPlus,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUser
} from 'react-icons/fa';

const ConvertCustomerToDealer = ({ adminApis, adminData, onBack, onDealerCreated }) => {
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!adminApis) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('Fetching customers and dealers...');
      
      // Fetch both customers and dealers to filter out existing dealers
      const [customersData, dealersData] = await Promise.all([
        adminApis.getAllCustomers(),
        adminApis.getDealers()
      ]);
      
      console.log('Customers data:', customersData);
      console.log('Dealers data:', dealersData);
      
      // Create a set of dealer emails for quick lookup
      const dealerEmails = new Set(dealersData.map(dealer => dealer.email));
      
      // Filter out customers who are already dealers (B2B type or in dealer emails)
      const nonDealerCustomers = customersData.filter(customer => 
        customer.customer_type !== 'B2C' && !dealerEmails.has(customer.email)
      );
      
      setCustomers(nonDealerCustomers);
      setDealers(dealersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [adminApis]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter customers based on search query
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchQuery || 
      (customer.first_name && customer.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (customer.last_name && customer.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });

  const handleConvertCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowConvertModal(true);
    setConvertError(null);
  };

  const handleConfirmConvert = async () => {
    if (!selectedCustomer || !adminApis) {
      setConvertError('Customer data or API not available');
      return;
    }

    setConverting(true);
    setConvertError(null);

    try {
      // Parse address to extract components
      const addressParts = (selectedCustomer.customer_location || '').split(', ');
      let address = '', city = '', state = '', zipCode = '';
      
      if (addressParts.length >= 3) {
        address = addressParts.slice(0, -2).join(', ');
        city = addressParts[addressParts.length - 2];
        const stateZip = addressParts[addressParts.length - 1];
        
        // Try to extract state and zip from "STATE ZIP" format
        const stateZipMatch = stateZip.match(/^([A-Z]{2})\s+(.+)$/);
        if (stateZipMatch) {
          state = stateZipMatch[1];
          zipCode = stateZipMatch[2];
        } else {
          // Fallback: assume last part is zip, everything else is state
          const parts = stateZip.split(' ');
          if (parts.length > 1) {
            zipCode = parts[parts.length - 1];
            state = parts.slice(0, -1).join(' ');
          } else {
            state = stateZip;
          }
        }
      } else if (addressParts.length === 2) {
        address = addressParts[0];
        city = addressParts[1];
      } else if (addressParts.length === 1) {
        address = addressParts[0];
      }

      const dealerData = {
        storeName: selectedCustomer.first_name || 'Store',
        contactPerson: selectedCustomer.last_name || 'Contact Person',
        email: selectedCustomer.email,
        phone: selectedCustomer.phone_number || '',
        address: address,
        city: city,
        state: state,
        zipCode: zipCode
      };

      console.log('Converting customer to dealer:', dealerData);

      const result = await adminApis.convertCustomerToDealer(selectedCustomer.id, dealerData);

      if (result.success) {
        console.log('Customer converted to dealer successfully:', result.customer);
        
        // Notify parent component
        if (onDealerCreated) {
          onDealerCreated(result.customer);
        }
        
        // Close modal and refresh data
        setShowConvertModal(false);
        setSelectedCustomer(null);
        fetchData();
        
        // Show success and navigate back after delay
        setTimeout(() => {
          if (onBack) {
            onBack();
          }
        }, 2000);
      } else {
        setConvertError(result.error || 'Failed to convert customer to dealer. Please try again.');
      }
    } catch (error) {
      console.error('Error converting customer to dealer:', error);
      setConvertError('Network error while converting customer. Please check your connection and try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleCancelConvert = () => {
    setShowConvertModal(false);
    setSelectedCustomer(null);
    setConvertError(null);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleBack}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <FaArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Convert Customer to Dealer</h1>
          <p className="text-gray-600 mt-1">Select an existing customer to convert to a dealer</p>
          {adminData?.store?.storeName && (
            <p className="text-sm text-gray-500 mt-1">
              Store: {adminData.store.storeName}
            </p>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center space-x-2">
            <FaUsers className="text-gray-400" />
            <span className="text-lg font-semibold text-gray-900">
              Available Customers ({filteredCustomers.length})
            </span>
          </div>
          
          <div className="relative w-full sm:w-auto">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
            />
          </div>
        </div>
      </div>

      {/* Customers List */}
      {filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <FaUsers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No customers available</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery 
                ? 'No customers match your search criteria.'
                : 'All customers have already been converted to dealers or no customers exist.'
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <FaUser className="text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.first_name} {customer.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {customer.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <FaEnvelope className="mr-2 text-gray-400" />
                          {customer.email || 'No email'}
                        </div>
                        <div className="flex items-center mt-1">
                          <FaPhone className="mr-2 text-gray-400" />
                          {customer.phone_number || 'No phone'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-start">
                          <FaMapMarkerAlt className="mr-2 text-gray-400 mt-0.5" />
                          <span>{customer.customer_location || 'No address'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleConvertCustomer(customer)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                      >
                        <FaUserPlus className="mr-2" />
                        Convert to Dealer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Convert Confirmation Modal */}
      {showConvertModal && selectedCustomer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <FaUserPlus className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">
                Convert Customer to Dealer
              </h3>
              <div className="mt-4 text-left">
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to convert this customer to a dealer?
                </p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{selectedCustomer.email}</p>
                  <p className="text-sm text-gray-600">{selectedCustomer.phone_number}</p>
                </div>
                
                {convertError && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{convertError}</p>
                  </div>
                )}
              </div>
              
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancelConvert}
                    disabled={converting}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmConvert}
                    disabled={converting}
                    className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {converting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Converting...
                      </div>
                    ) : (
                      'Convert to Dealer'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvertCustomerToDealer;
